import type { AlgoliaSearchHelper } from "algoliasearch-helper";
import { RANGE_META } from "@/services/algoliaRouting.core";
import {
    curatedSearchConfig,
    rangeFiltersState,
} from "@/services/algoliaRouting.blade";
import { familyOf } from "@/marketplace/model-families";

type FacetRefinements = Record<string, string[]> | undefined;

/** Clés « facette:valeur » d'un état de facettes (menu, refinementList, état de l'helper). */
function addFacetKeys(keys: Set<string>, refinements: FacetRefinements): void {
    if (!refinements) return;
    for (const [attribute, values] of Object.entries(refinements)) {
        for (const value of Array.isArray(values) ? values : [values]) {
            if (value == null || value === "") continue;
            // Une famille de modèles (Cayenne II et III) compte pour un filtre, comme « Modèle (1) »
            keys.add(`${attribute}:${attribute === "model" ? familyOf(String(value)) : value}`);
        }
    }
}

/** Filtres imposés par la landing ou la recherche curée, en clés uniques. */
function curatedBaselineKeys(): Set<string> {
    const keys = new Set<string>();
    const config = curatedSearchConfig.value;
    if (!config.isActive || !config.filters) return keys;

    const filters = config.filters;
    addFacetKeys(
        keys,
        Object.fromEntries(
            Object.entries(filters.menu ?? {}).map(([attribute, value]) => [attribute, [value]]),
        ),
    );
    addFacetKeys(keys, filters.refinementList);

    for (const { attribute, minKey, maxKey } of RANGE_META) {
        if ((minKey && filters[minKey] !== undefined) || filters[maxKey] !== undefined) {
            keys.add(`range:${attribute}`);
        }
    }

    return keys;
}

/** Landing / curated defaults (make, model, etc.) applied via configure.filters. */
export function countCuratedBaselineFilters(): number {
    return curatedBaselineKeys().size;
}

/** Active filter pills rendered by InstantSearch + range/spec widgets. */
export function countVisibleActiveFilterPills(
    root?: ParentNode,
): number {
    const scope =
        root ?? (typeof document !== "undefined" ? document : null);
    if (!scope) return 0;

    return scope.querySelectorAll(
        "#current-refinements .current-refinements__item, #range-filter-pills .current-refinements__item",
    ).length;
}

/**
 * Nombre de filtres actifs du bouton « Filtrer ». Une valeur imposée par la page (marque d'une
 * landing, plage d'une page dynamique) est aussi dans l'état de recherche : chaque filtre est
 * compté une fois, qu'il vienne de la page ou du visiteur.
 */
export function countActiveMarketplaceFilters(
    helper?: AlgoliaSearchHelper | null,
): number {
    const keys = curatedBaselineKeys();

    if (helper) {
        const state = helper.state;
        addFacetKeys(keys, state.facetsRefinements as FacetRefinements);
        addFacetKeys(keys, state.disjunctiveFacetsRefinements as FacetRefinements);
        addFacetKeys(keys, state.hierarchicalFacetsRefinements as FacetRefinements);
    }

    for (const { attribute } of RANGE_META) {
        const range = rangeFiltersState.value[attribute];
        if (range && (range.min !== undefined || range.max !== undefined)) {
            keys.add(`range:${attribute}`);
        }
    }

    if (rangeFiltersState.value.specification?.trim()) {
        keys.add("specification");
    }

    return keys.size;
}

export function formatFilterCountSuffix(count: number): string {
    return count > 0 ? ` (${count})` : "";
}

export function updateFilterToggleLabels(
    count: number,
    root: ParentNode = document,
): void {
    const suffix = formatFilterCountSuffix(count);

    root.querySelectorAll<HTMLElement>("[data-filter-label]").forEach((el) => {
        const base = el.dataset.filterLabel ?? el.textContent?.trim() ?? "";

        // Barre d'actions : le nombre en pastille (« Filtrer ② ») plutôt qu'entre parenthèses
        if (el.closest(".marketplace-action-bar")) {
            el.textContent = base;
            if (count > 0) {
                const badge = document.createElement("span");
                badge.className = "marketplace-action-bar__badge";
                badge.textContent = String(count);
                el.append(badge);
            }
            el.closest("button")?.setAttribute(
                "aria-label",
                count > 0
                    ? `Ouvrir les filtres, ${count} actif${count > 1 ? "s" : ""}`
                    : "Ouvrir les filtres",
            );
            return;
        }

        el.textContent = base + suffix;
    });
}
